import { blogPostsLocales, guidesLocales } from '@/config/locales';
import { mapApiDataToBlogHome } from '@/lib/cms-mappers';
import { PAGE_SIZE } from '@/config/strapi-content';

import type { FBlogHomeTemplateProps } from '@/components/templates/FBlogHomeTemplate.vue';

const populateFields = [
  'main_image',
  'audiences',
  'main_topic',
  'other_topics',
];

/**
 * Returns blog posts data fetched from CMS (Strapi)
 */
export async function useCmsBlogHome(): UseCmsResponse<FBlogHomeTemplateProps> {
  const { t } = useI18n();

  const blogPosts = useStrapi('blog-posts', {
    localesSubset: blogPostsLocales,
    queryParams: {
      populate: populateFields.join(','),
      sort: { published_at_override: 'desc', publishedAt: 'desc' },
      pagination: {
        page: 0,
        pageSize: PAGE_SIZE,
      },
    },
  });
  const audiences = useStrapi('audiences', {
    localesSubset: blogPostsLocales,
  });
  const topics = useStrapi('topics', {
    localesSubset: blogPostsLocales,
  });
  const guides = useStrapi('guides', {
    localesSubset: guidesLocales,
    queryParams: {
      populate: '*',
      pagination: {
        page: 0,
        pageSize: PAGE_SIZE,
      },
    },
  });

  const [
    { data: blogPostsData },
    { data: audiencesData },
    { data: topicsData },
    { data: guidesData },
  ] = await Promise.all([blogPosts, audiences, topics, guides]);

  const sortedBlogPostData = computed(() =>
    blogPostsData.value?.data.sort((itemA, itemB) =>
      sortByPublishedDateDesc(itemA.attributes, itemB.attributes)
    )
  );

  const mappedData = computed(() =>
    mapApiDataToBlogHome(
      sortedBlogPostData.value,
      audiencesData.value?.data,
      topicsData.value?.data,
      guidesData.value?.data
    )
  );

  const i18nMappedData = computed<FBlogHomeTemplateProps>(() => ({
    ...mappedData.value,
    blogPostCards: mappedData.value.blogPostCards.map(blogPostCard => ({
      ...blogPostCard,
      linkText: t('blog__related_posts__cards__button_text'),
    })),
  }));

  return { data: i18nMappedData };
}

/**
 * Sort the blog posts by published date, descending
 * @param blogPostA - blog post A used for sorting
 * @param blogPostB - blog post B used for sorting
 */
function sortByPublishedDateDesc(
  blogPostA: BlogPostContentType,
  blogPostB: BlogPostContentType
): number {
  const dateA = new Date(
    blogPostA.published_at_override || blogPostA.publishedAt
  ).getTime();
  const dateB = new Date(
    blogPostB.published_at_override || blogPostB.publishedAt
  ).getTime();

  return dateB - dateA;
}
